import { advisorPortalConfigurationApi, usersManagementApi } from 'app/services/api';
import _ from 'lodash';

import { DeletedUsersPolicy, UserSearchField } from 'constants/userSearch';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const ADVISOR_PORTAL_CONFIGURATIONS_ACTION_TYPE = createNamedActions(
  'advisorPortalConfigurations',
  {
    GET_LIST: 'GET_LIST',
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
  },
);

export const getAdvisorPortalConfigurationsAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await advisorPortalConfigurationApi.getAdvisorPortalConfigurations();

  return dispatch({
    type: ADVISOR_PORTAL_CONFIGURATIONS_ACTION_TYPE.GET_LIST,
    payload: data,
  });
});

export const createAdvisorPortalConfigurationAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await advisorPortalConfigurationApi.createAdvisorPortalConfiguration(params);

    return dispatch({
      type: ADVISOR_PORTAL_CONFIGURATIONS_ACTION_TYPE.CREATE,
      payload: data,
    });
  },
);

export const updateAdvisorPortalConfigurationAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await advisorPortalConfigurationApi.updateAdvisorPortalConfiguration(params);

    return dispatch({
      type: ADVISOR_PORTAL_CONFIGURATIONS_ACTION_TYPE.UPDATE,
      payload: data,
    });
  },
);

export const deleteAdvisorPortalConfigurationAction = catchAsyncActionError(
  ({ id }) =>
    async dispatch => {
      await advisorPortalConfigurationApi.deleteAdvisorPortalConfiguration({ id });

      return dispatch({
        type: ADVISOR_PORTAL_CONFIGURATIONS_ACTION_TYPE.DELETE,
        payload: { id },
      });
    },
);

export const uploadAdvisorPortalConfigurationImageAction = catchAsyncActionError(
  ({ id, type, formData }) =>
    async () => {
      const { data } = await advisorPortalConfigurationApi.uploadAdvisorPortalConfigurationImage({
        id,
        type,
        formData,
      });

      return { data, type };
    },
);

export const getAdvisorPortalConfigurationAction = catchAsyncActionError(({ id }) => async () => {
  const { data } = await advisorPortalConfigurationApi.getAdvisorPortalConfiguration({ id });

  return data;
});

export const getAdvisorPortalConfigurationDomainsAction = catchAsyncActionError(
  ({ id }) =>
    async () => {
      const { data } = await advisorPortalConfigurationApi.getAdvisorPortalConfigurationDomains({
        id,
      });

      return data;
    },
);

export const assignAdvisorPortalConfigurationDomainAction = catchAsyncActionError(
  ({ id, domainName }) =>
    async () => {
      const { data } = await advisorPortalConfigurationApi.assignAdvisorPortalConfigurationDomain({
        id,
        domainName,
      });

      return data;
    },
);

export const removeAdvisorPortalConfigurationDomainAction = catchAsyncActionError(
  ({ id, domainName }) =>
    async () => {
      await advisorPortalConfigurationApi.removeAdvisorPortalConfigurationDomain({
        id,
        domainName,
      });

      return { domainName };
    },
);

// Mirrors getRoleUsersAction in store/system/actions/role.users.actions.
const fetchUsersForSecurityGroupIds = async ({ ids, companyId }) => {
  if (!ids.length) {
    return [];
  }

  const { data } = await usersManagementApi.searchUsers({
    fields: [
      UserSearchField.ID,
      UserSearchField.FIRST_NAME,
      UserSearchField.MIDDLE_INITIAL,
      UserSearchField.LAST_NAME,
      UserSearchField.SUFFIX,
      UserSearchField.EMAIL_ADDRESS,
    ],
    filter: {
      companyId,
      roleIds: ids,
      excludeSubCompanyUsers: true,
      deletedPolicy: DeletedUsersPolicy.EXCLUDE_DELETED,
    },
  });

  return data.items;
};

// Normalizes the Accounts user shape (mirrors selectExtendedUsers in store/users/selectors).
const normalizeAdvisorUser = user => ({
  id: user.id,
  name: [user.firstName, user.middleInitial, user.lastName, user.suffix].filter(Boolean).join(' '),
  email: user.emailAddress ?? user.email ?? '',
});

// Fetches + merges the enrolled advisors for an AdvisorPortalConfiguration's Member/Admin security
// groups. `groups` is [{ ids: [securityGroupId], purpose }] — purpose is an opaque passthrough
// label (this store slice has no opinion on what "Member"/"Admin" means). A user in more than one
// group is returned once, with all of their purposes.
export const getAdvisorPortalConfigurationAdvisorsAction = catchAsyncActionError(
  ({ companyId, groups = [] }) =>
    async () => {
      const usersByPurpose = await Promise.all(
        groups.map(async ({ ids, purpose }) => ({
          purpose,
          users: await fetchUsersForSecurityGroupIds({ ids: _.uniq(ids), companyId }),
        })),
      );

      const advisorsById = new Map();

      usersByPurpose.forEach(({ purpose, users }) => {
        users.forEach(user => {
          const existing = advisorsById.get(user.id);

          if (existing) {
            existing.purposes = _.uniq([...existing.purposes, purpose]);

            return;
          }

          advisorsById.set(user.id, { ...normalizeAdvisorUser(user), purposes: [purpose] });
        });
      });

      return Array.from(advisorsById.values());
    },
);
